import {
  CashAmounts,
  ICashFloat,
  ICashSummary,
  ICashTransfer,
  IUserSwitch,
} from "@/interfaces/cash-float/cash-float";
import CashFloatModule from "@/store/modules/cash-float";
import POSConfigModule from "@/store/modules/pos-config";
import UserModule from "@/store/modules/user";
import DeviceModule from "@/store/modules/device";
import { isBelgianCompliant } from "./compliance";
import dayjs from "dayjs";
import { showConfirm } from "@/utils/vue-helpers/confirm-dialog";
import i18n from "@/lang";
// Simplified type alias to avoid TS2589 (excessively deep type instantiation)
// caused by vue-i18n v11's complex overloaded generics on t()
const t: (key: string, ...args: unknown[]) => string = (i18n.global as any).t;
import { nowDB } from "../helpers/dates";
import { getUntypedBookingsByCashfloat } from "@/db/product-slot-booking";
import {
  AccountingState,
  IAccounting,
  PaymentMethods,
} from "@/types/db/accounting";
import { sumArray } from "../helpers/common";
import { LogLevel, LogService } from "@/services/logging";
import { computed } from "vue";
import ConfirmLogout from "@/components/common/ConfirmLogout.vue";
import ConfirmClosingSales from "@/components/common/ConfirmClosingSales.vue";
import ModalModule from "@/store/modules/modal";
import {
  pushSyncCashfloat,
  pushSyncCashTransfer,
} from "@/api/cash-float/cash-float";
import { SharedCallbacks } from "../shared-resources";
import { closeCashPoint } from "../helpers/fiskaly";
import { Currencies, Currency } from "@/constants/currency";
import { hasNonEmptyBookings } from "./booking";
import NotificationService from "@/services/notification.service";
import { getCashfloatByUidFromBackend } from "@/api/gql/cashfloat";
import { isCashbookV2Enabled } from "@/utils/helpers/feature-flags";

export async function openTopUpForm() {
  if (isCashbookV2Enabled()) {
    // Cashbook V2 opening flow — currently identical to legacy; will diverge
    // as subsequent PRs land the new opening UI behind this gate.
    CashFloatModule.setIsOpeningCashfloat(true);
    return;
  }
  CashFloatModule.setIsOpeningCashfloat(true);
}

// add current user to the userSwitch array if there's a cashfloat already open
export async function addToUserSwitch() {
  const cashFloat = CashFloatModule.cashFloat;
  if (!cashFloat) {
    return;
  }
  try {
    // confirm if cashfloat is still open and can be used by another user
    await checkCashfloatOnBackend(cashFloat);
  } catch (error) {
    LogService.info(
      `Error when checking cashfloat with uuid ${cashFloat.uid} on backend`,
      {
        error,
      },
      LogLevel.Error
    );
  }
  const userId = UserModule.userId;
  cashFloat.userSwitch.push({
    user_id: userId as number,
    datetime: nowDB(),
    //validAmounts: cashFloat.validAmounts || null,
    //amounts: cashFloat.amounts,
    //messages: cashFloat.messages || {},
  });
  LogService.info(
    `User is assigned to pre-existing cashfoat with uuid ${cashFloat.uid}`,
    {
      cashfloat: cashFloat,
      userId,
    }
  );
}

export async function checkCashfloatOnBackend(cashFloat: ICashFloat) {
  // check asynchronously if cashfloat exists on backend with closeDate set, raise an alert if so
  // This is in case the app wasn't closed properly and cashfloat was synced to backend but not closed on device
  const cf = await getCashfloatByUidFromBackend(cashFloat.uid);
  if (cf && cf.closeDate) {
    LogService.info(
      `ALERT: Cashfloat with uid ${cashFloat.uid} is closed on backend but open on device!`,
      {
        cashfloatBackend: cf,
        cashfloatDevice: cashFloat,
      }
    );
    CashFloatModule.setCashFloat(null);
  }
}

export async function getCashBookingsAmount(
  currency: Currency
): Promise<number> {
  // get all cash accountings of booking from indexeddb with this cashfloat uuid
  // and calculate the sum of the accEntries
  const cashfloatUuid = String(CashFloatModule.cashFloat?.uid);
  // cashfloat bookings should come from two db (psb db and untypedpsb db)
  const cashfloatBookings = await getUntypedBookingsByCashfloat(cashfloatUuid);
  const cashAccountings = cashfloatBookings
    .map((b) => b.accountings)
    .flat()
    .filter(
      (acc) =>
        acc.paymentMethod === PaymentMethods.METHOD_POS_CASH &&
        acc.state === AccountingState.Completed &&
        acc.CfUid === cashfloatUuid
    );
  // Remove duplicate from cashAccountings (case of edit order will return the booking twice from the db)
  // knowing that two accountings with the same uuid are considered as duplicate
  const uniqCashAccountings: IAccounting[] = [];
  for (let i = 0; i < cashAccountings.length; i++) {
    const index = uniqCashAccountings.findIndex(
      (acc) => acc.uuid === cashAccountings[i].uuid
    );
    if (index > -1) {
      // accountings already pushed to uniqCashAccountings, do nothing
      continue;
    } else {
      uniqCashAccountings.push(cashAccountings[i]);
    }
  }

  LogService.info(
    `Attached accountings considered when calculating amount of cashfloat: ${cashfloatUuid}`,
    {
      params: uniqCashAccountings.map((acc) => acc.uuid),
    }
  );
  const cashAccEntriesValues = uniqCashAccountings
    .map((acc) => acc.accountingEntriesList)
    .flat()
    .map((ae) => ae.paymentCurrencyValue || ae.value);

  const startAmount =
    CashFloatModule.cashFloat?.startAmounts[currency.label] || 0;
  const cashfloatAmount = sumArray(cashAccEntriesValues) + startAmount;

  return cashfloatAmount;
}

export async function getCashBookingsAmountWithoutRefund(): Promise<CashAmounts> {
  // get all cash accountings of booking from indexeddb with this cashfloat uuid, refund are not considered
  // and calculate the sum of the accEntries
  const cashfloatUuid = String(CashFloatModule.cashFloat?.uid);
  // cashfloat bookings should come from two db (psb db and untypedpsb db)
  const cashfloatBookings = await getUntypedBookingsByCashfloat(cashfloatUuid);
  const cashAccountings = cashfloatBookings
    .map((b) => b.accountings)
    .flat()
    .filter(
      (acc) =>
        acc.paymentMethod === PaymentMethods.METHOD_POS_CASH &&
        acc.state === AccountingState.Completed &&
        acc.CfUid === cashfloatUuid &&
        acc.refund !== 1
    );
  // Remove duplicate from cashAccountings (case of edit order will return the booking twice from the db)
  // knowing that two accountings with the same uuid are considered as duplicate
  const uniqCashAccountings: IAccounting[] = [];
  for (let i = 0; i < cashAccountings.length; i++) {
    const index = uniqCashAccountings.findIndex(
      (acc) => acc.uuid === cashAccountings[i].uuid
    );
    if (index > -1) {
      // accountings already pushed to uniqCashAccountings, do nothing
      continue;
    } else {
      uniqCashAccountings.push(cashAccountings[i]);
    }
  }

  const cashSales: CashAmounts = {};
  const mainCurrency =
    (POSConfigModule.groupInfo?.currency as Currencies) || Currencies.CHF;

  uniqCashAccountings.forEach((accounting) => {
    let currency = (accounting.currency as Currencies) || mainCurrency;
    const paymentCurrency = accounting.paymentCurrency;
    if (!paymentCurrency) {
      LogService.info(
        `WARNING empty paymentCurrency in accounting with uuid: ${accounting.uuid}`
      );
    }
    if (paymentCurrency !== currency) {
      // for secondary currencies we take the value of provided cash bills
      if (paymentCurrency) {
        currency = paymentCurrency;
      }
      if (!cashSales[currency]) {
        cashSales[currency] = (accounting.providedCashBill || 0) / 100;
      } else {
        cashSales[currency]! += (accounting.providedCashBill || 0) / 100;
      }
    } else {
      // for main currency we take the sum of accouting entries value
      accounting.accountingEntriesList.forEach((accountingEntry) => {
        if (paymentCurrency) {
          currency = paymentCurrency;
        }
        if (!cashSales[currency]) {
          cashSales[currency] = (accountingEntry.value || 0) / 100;
        } else {
          cashSales[currency]! += (accountingEntry.value || 0) / 100;
        }
      });
    }
  });

  return cashSales;
}

export async function getCashRefund(): Promise<number> {
  // get all cashrefund  accountings of booking from indexeddb with this cashfloat uuid
  // and calculate the sum of the accEntries
  const cashfloatUuid = String(CashFloatModule.cashFloat?.uid);
  // cashfloat bookings should come from two db (psb db and untypedpsb db)
  const cashfloatBookings = await getUntypedBookingsByCashfloat(cashfloatUuid);
  const cashAccountings = cashfloatBookings
    .map((b) => b.accountings)
    .flat()
    .filter(
      (acc) =>
        acc.paymentMethod === PaymentMethods.METHOD_POS_CASH &&
        acc.state === AccountingState.Completed &&
        acc.CfUid === cashfloatUuid &&
        acc.refund === 1
    );
  // Remove duplicate from cashAccountings (case of edit order will return the booking twice from the db)
  // knowing that two accountings with the same uuid are considered as duplicate
  const uniqCashAccountings: IAccounting[] = [];
  for (let i = 0; i < cashAccountings.length; i++) {
    const index = uniqCashAccountings.findIndex(
      (acc) => acc.uuid === cashAccountings[i].uuid
    );
    if (index > -1) {
      // accountings already pushed to uniqCashAccountings, do nothing
      continue;
    } else {
      uniqCashAccountings.push(cashAccountings[i]);
    }
  }
  const cashAccEntriesValues = uniqCashAccountings
    .map((acc) => acc.accountingEntriesList)
    .flat()
    .map((ae) => ae.value);

  const cashSalesAmountWithoutRefund = sumArray(cashAccEntriesValues);

  return -1 * cashSalesAmountWithoutRefund;
}

export async function getCashChange(): Promise<number> {
  // get all cashrefund  accountings of booking from indexeddb with this cashfloat uuid
  // and calculate the sum of the accEntries
  const cashfloatUuid = String(CashFloatModule.cashFloat?.uid);
  // cashfloat bookings should come from two db (psb db and untypedpsb db)
  const cashfloatBookings = await getUntypedBookingsByCashfloat(cashfloatUuid);
  const cashAccountings = cashfloatBookings
    .map((b) => b.accountings)
    .flat()
    .filter(
      (acc) =>
        acc.paymentMethod === PaymentMethods.METHOD_POS_CASH &&
        acc.state === AccountingState.Completed &&
        acc.CfUid === cashfloatUuid &&
        acc.refund !== 1
    );
  // Remove duplicate from cashAccountings (case of edit order will return the booking twice from the db)
  // knowing that two accountings with the same uuid are considered as duplicate
  const uniqCashAccountings: IAccounting[] = [];
  for (let i = 0; i < cashAccountings.length; i++) {
    const index = uniqCashAccountings.findIndex(
      (acc) => acc.uuid === cashAccountings[i].uuid
    );
    if (index > -1) {
      // accountings already pushed to uniqCashAccountings, do nothing
      continue;
    } else {
      uniqCashAccountings.push(cashAccountings[i]);
    }
  }
  const changeGivenBack = uniqCashAccountings.map((acc) => {
    return acc.paymentAndChange?.changeValue || 0;
  });

  const sumOfChange = sumArray(changeGivenBack);

  return sumOfChange;
}

export const hasSalesStarted = computed(() => {
  // return false;
  return CashFloatModule.cashFloat;
});

/**
 * Confirm sign-out while a cash float is still open.
 *
 * @returns {Promise<boolean>} Resolves `true` if the user confirms sign-out.
 */
export async function confirmLogout(): Promise<boolean> {
  ModalModule.setShallLogout(true);
  return showConfirm({
    title: t("nav.sign-out"),
    body: ConfirmLogout,
    variant: hasNonEmptyBookings.value ? "destructive" : "default",
    confirmText: t("nav.sign-out"),
  });
}

/**
 * Confirm closing sales when there are incomplete or partially paid bookings.
 *
 * @returns {Promise<boolean>} Resolves `true` if the user confirms.
 */
export async function confirmClosingSales(): Promise<boolean> {
  return showConfirm({
    title: t("nav.close-sales"),
    body: ConfirmClosingSales,
    variant: "destructive",
    confirmText: t("button.confirm"),
    cancelText: t("button.back"),
  });
}

// If the user has Fiskaly logic enabled then we disable multi currency logic for him
export const hasMultiCurrenciesEnabled = computed(() => {
  const whitelistedGroups = [19469];
  if (whitelistedGroups.includes(POSConfigModule.groupId || 0)) {
    return true;
  }
  if (DeviceModule.hardwareLocation?.deviceId) {
    return false;
  }
  // Belgian fiscal reporting assumes a single currency, so the currency
  // selector is hidden from the cashfloat and payment flows there.
  if (isBelgianCompliant.value) {
    return false;
  }
  return true;
});

/**
 * Synchronizes cash transfer, sends proper logs and inform user in case of failure
 * Returns true if the sync was successful, false otherwise.
 * @param cashTransfer
 */
export async function handleCashTransferSync(cashTransfer: ICashTransfer) {
  try {
    LogService.info(`Synching cash transfer with uuid: ${cashTransfer.uuid}`, {
      params: cashTransfer,
    });
    await pushSyncCashTransfer(cashTransfer);
    return true;
  } catch (err) {
    const isNetworkError = !err.response;
    const message = isNetworkError
      ? t("error.cash-transfer-network-error")
      : t("error.general-error");
    LogService.info(
      `Error when synching cash transfer with uuid: ${cashTransfer.uuid}`,
      {
        params: { error: err, cashTransfer: cashTransfer },
      }
    );
    NotificationService.showCustomMessageWithTitle(
      message,
      t("error.cash-transfer"),
      "error"
    );
    return false;
  }
}
